(() => {
  if (window.AndCartDrawerFallbackV1) return;
  window.AndCartDrawerFallbackV1 = true;

  const STYLE_ID = "and-cart-drawer-fallback-style";
  const PENDING_CLASS = "and-cart-pending";
  const FALLBACK_DELAY = 1800;
  const RELEASE_DELAY = 12000;

  if (!document.getElementById(STYLE_ID)) {
    const style = document.createElement("style");
    style.id = STYLE_ID;
    style.textContent = `
      #CartDrawer .${PENDING_CLASS} {
        opacity: .55;
        cursor: wait;
      }
      #CartDrawer .${PENDING_CLASS} button,
      #CartDrawer button.${PENDING_CLASS} {
        pointer-events: none;
      }
    `;
    document.head.appendChild(style);
  }

  const pending = new WeakMap();

  const root = () => window.Shopify?.routes?.root || "/";
  const cartEndpoint = (path) => `${root()}${path}`.replace(/\/{2,}/g, "/");
  const drawer = () => document.getElementById("CartDrawer");
  const sectionId = () => drawer()?.dataset.sectionId;

  const postJson = async (path, body) => {
    const response = await fetch(cartEndpoint(path), {
      method: "POST",
      credentials: "same-origin",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        "X-Requested-With": "XMLHttpRequest",
      },
      body: JSON.stringify(body),
    });

    const type = response.headers.get("content-type") || "";
    if (!type.includes("application/json")) {
      throw new Error("Cart request returned non-JSON response.");
    }

    const json = await response.json();
    if (!response.ok || json.errors) {
      throw new Error(json.errors || json.description || "Cart request failed.");
    }
    return json;
  };

  const sectionPayload = () => ({
    sections: [sectionId()],
    sections_url: window.location.pathname,
  });

  const renderSections = (data) => {
    const id = sectionId();
    const html = id && data?.sections?.[id];
    if (!html) return false;

    const doc = new DOMParser().parseFromString(html, "text/html");
    const nextDrawer = doc.getElementById("CartDrawer");
    const currentDrawer = drawer();
    if (!nextDrawer || !currentDrawer) return false;

    const wasOpen = !currentDrawer.hidden || currentDrawer.hasAttribute("open");
    currentDrawer.replaceWith(nextDrawer);

    if (typeof data.item_count === "number") {
      document.querySelectorAll("[data-cart-count],.cart-count,cart-count").forEach((node) => {
        node.textContent = data.item_count;
        node.hidden = data.item_count < 1;
      });
      window.AndskyCartCountSync?.updateCount?.(data.item_count);
    }

    if (wasOpen || data.item_count > 0) {
      nextDrawer.hidden = false;
      nextDrawer.removeAttribute("hidden");
      nextDrawer.setAttribute("open", "");
      nextDrawer.classList.add("is-open", "active");
      document
        .querySelectorAll('[aria-controls="CartDrawer"]')
        .forEach((node) => node.setAttribute("aria-expanded", "true"));
    }

    return true;
  };

  const release = (element) => {
    const state = pending.get(element);
    if (!state) return;

    window.clearTimeout(state.releaseTimer);
    window.clearTimeout(state.fallbackTimer);
    element.classList.remove(PENDING_CLASS);
    element.removeAttribute("aria-busy");

    state.controls.forEach((control) => {
      if (control.dataset.andPendingWasDisabled !== "true") control.disabled = false;
      delete control.dataset.andPendingWasDisabled;
    });

    pending.delete(element);
  };

  const markPending = (element, fallback) => {
    if (!element || pending.has(element)) return;

    const controls = element.matches("button, input, select, textarea")
      ? [element]
      : Array.from(element.querySelectorAll("button, input, select, textarea"));

    element.classList.add(PENDING_CLASS);
    element.setAttribute("aria-busy", "true");

    window.requestAnimationFrame(() => {
      controls.forEach((control) => {
        control.dataset.andPendingWasDisabled = control.disabled ? "true" : "false";
        control.disabled = true;
      });
    });

    const fallbackTimer = window.setTimeout(async () => {
      if (!document.documentElement.contains(element)) return;

      try {
        const data = await fallback();
        renderSections(data);
      } catch (error) {
        console.error("[cart drawer fallback]", error);
        release(element);
      }
    }, FALLBACK_DELAY);

    const releaseTimer = window.setTimeout(() => release(element), RELEASE_DELAY);
    pending.set(element, { controls, fallbackTimer, releaseTimer });
  };

  const fallbackChangeLine = (key, quantity) =>
    postJson("cart/change.js", {
      id: key,
      quantity,
      ...sectionPayload(),
    });

  const fallbackAddVariant = (variantId, quantity = 1, properties = {}) =>
    postJson("cart/add.js", {
      id: Number(variantId),
      quantity,
      properties,
      ...sectionPayload(),
    });

  const fallbackChooseGift = async (variantId) => {
    const currentDrawer = drawer();
    const giftIds = Array.from(currentDrawer?.querySelectorAll("[data-and-gift-variant]") || [])
      .map((button) => button.dataset.andGiftVariant)
      .filter(Boolean)
      .map(String);

    const cart = await fetch(cartEndpoint("cart.js"), {
      credentials: "same-origin",
      headers: { Accept: "application/json" },
    }).then((response) => response.json());

    const updates = {};
    for (const item of cart.items || []) {
      if (giftIds.includes(String(item.variant_id))) updates[item.key] = 0;
    }
    updates[variantId] = 1;

    return postJson("cart/update.js", {
      updates,
      ...sectionPayload(),
    });
  };

  document.addEventListener(
    "click",
    (event) => {
      const currentDrawer = event.target.closest("#CartDrawer");
      if (!currentDrawer) return;

      const qtyButton = event.target.closest("[data-and-cart-qty][data-and-cart-key]");
      if (qtyButton) {
        const key = qtyButton.dataset.andCartKey;
        const quantity = Math.max(0, Number(qtyButton.dataset.andCartQty || 0));
        markPending(qtyButton.closest(".and-cart-qty") || qtyButton, () =>
          fallbackChangeLine(key, quantity)
        );
        return;
      }

      const giftButton = event.target.closest("[data-and-gift-variant]");
      if (giftButton && !giftButton.classList.contains("is-selected") && !giftButton.disabled) {
        const variantId = giftButton.dataset.andGiftVariant;
        markPending(giftButton.closest(".and-cart-gifts") || giftButton, () =>
          fallbackChooseGift(variantId)
        );
        return;
      }

      const removeButton = event.target.closest("[data-and-cart-remove]");
      if (removeButton) {
        const key = removeButton.dataset.andCartRemove;
        markPending(removeButton, () => fallbackChangeLine(key, 0));
        return;
      }

      const recButton = event.target.closest("[data-and-rec-add]");
      if (recButton && !recButton.disabled) {
        const variantId = recButton.dataset.andRecAdd;
        markPending(recButton, () => fallbackAddVariant(variantId));
      }
    },
    true
  );
})();
